import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { AdasBox, AdasCard, AdasTypography, AdasTooltip, AdasMenuList, AdasPaper, AdasPopper } from '@components/wrapper-components'
import theme from 'theme/theme'
import { useDeviceQueries } from '@hooks/useDeviceQueries'
import { useCenterStore } from '@caradasstore/CenterStore'
import { getAllProductTags, updateProductTags } from '../../../api/api'
import { currencyFormatter } from '../../../utils/utils'
import { Logger } from '../../../logger'

// Render a single item row
export const ItemRow = ({ item, isCustomItem, disabled, onClickEdit, isMobile, isQuantity, type, isCustomInvoice, customData }) => {
  const nameRef = useRef(null)
  const tagIconRef = useRef(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [tagOptions, setTagOptions] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [openTagFor, setOpenTagFor] = useState(null)

  const ccId = useCenterStore((store) => store.ccId)

  // Detect if text is truncated for tooltip
  useEffect(() => {
    if (nameRef.current) {
      setIsTruncated(nameRef.current.scrollWidth > nameRef.current.offsetWidth)
    }
  }, [isMobile])

  // Dynamic layout flex logic
  const getFlex = (column) => {
    const layout = { name: '1 1 50%', qty: '1 1 15%', unit: '1 1 15%', total: '1 1 20%' }
    return layout[column]
  }

  const handleTagClick = useCallback(async () => {
    const isOpening = openTagFor !== item.id
    if (!isOpening) {
      setOpenTagFor(null)
      return
    }

    try {
      const response = await getAllProductTags({ ccid: ccId })
      if (response.status === 200 && Array.isArray(response.data)) {
        setTagOptions(response.data)
        setAnchorEl(tagIconRef.current)
        setOpenTagFor(item.id)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ItemRow > getAllProductTags' } })
    }
  }, [ccId, openTagFor, item.id])

  const handleTagSelection = async (itemId, tag) => {
    try {
      const args = [itemId, tag.id, type, customData?.id].filter(Boolean)
      await updateProductTags(...args)
      item.tag = tag
      setOpenTagFor(null)
      setAnchorEl(null)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ItemRow > updateProductTags' } })
    }
  }

  const tagMenuItems = useMemo(
    () =>
      tagOptions.map((tag) => ({
        label: tag.name,
        onClick: () => handleTagSelection(item.id, tag)
      })),
    [tagOptions, item.id]
  )

  const truncationStyles = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }

  return (
    <AdasCard
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '7px 15px',
        marginBottom: '8px',
        border: '1px solid #E0E0E0',
        boxShadow: 'none',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}
    >
      {/* Name + Tag column */}
      <AdasBox sx={{ flex: isMobile ? '1 1 25%' : getFlex('name'), minWidth: 0 }}>
        <AdasBox
          display='flex'
          gap={{ xs: 1, md: 2 }}
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <AdasTooltip title={item.name} disableHoverListener={!isTruncated}>
            <AdasTypography ref={nameRef} sx={{ ...truncationStyles, width: { xs: '100%', md: 'fit-content' } }} variant='body2'>
              {item.name}
            </AdasTypography>
          </AdasTooltip>

          {type === 'calibration' && (
            <AdasBox
              display='flex'
              alignItems='center'
              gap={0.5}
              onClick={disabled ? undefined : handleTagClick}
              sx={{ cursor: disabled ? 'default' : 'pointer' }}
            >
              {(item.tag?.name || !disabled) && (
                <AdasTooltip title={item?.tag?.name} disableHoverListener={!isTruncated}>
                  <AdasTypography
                    variant='subtitle2'
                    sx={{
                      color: item.tag?.name ? theme.palette.text.secondary : theme.typography.warning,
                      ...truncationStyles,
                      width: 'fit-content',
                      maxWidth: { xs: '70px', md: '95px', lg: '150px' } // Limit width to prevent overflow
                    }}
                  >
                    ({item.tag?.name || 'Select Tag'})
                  </AdasTypography>
                </AdasTooltip>
              )}
              {!disabled && <LocalOfferIcon ref={tagIconRef} color='primary' fontSize='small' sx={{ ml: { xs: '4px', md: '8px' } }} />}
              <AdasPopper open={openTagFor === item.id} anchorEl={anchorEl} placement='bottom'>
                <AdasPaper>
                  <AdasMenuList
                    items={tagMenuItems}
                    variant='withClickAway'
                    onClickAway={() => {
                      setOpenTagFor(null)
                      setAnchorEl(null)
                    }}
                  />
                </AdasPaper>
              </AdasPopper>
            </AdasBox>
          )}
        </AdasBox>
      </AdasBox>

      {/* Qty column */}
      <AdasBox
        sx={{
          flex: isMobile ? '1 1 25%' : getFlex('qty'),
          minWidth: 0,
          display: 'flex',
          justifyContent: { xs: 'center', md: 'end' }
        }}
      >
        <AdasTypography sx={truncationStyles} variant='body2'>
          {item.quantity}
        </AdasTypography>
      </AdasBox>

      {/* Unit price for custom invoice */}
      {isCustomInvoice && (
        <AdasBox sx={{ flex: '1 1 15%', minWidth: 0, display: 'flex', justifyContent: 'right' }}>
          <AdasTypography sx={truncationStyles} variant='body2'>
            {currencyFormatter(item.cost_price)}
          </AdasTypography>
        </AdasBox>
      )}

      {/* Total + edit icon */}
      <AdasBox
        sx={{
          flex: isMobile ? '1 1 25%' : getFlex('total'),
          minWidth: 0,
          display: 'flex',
          justifyContent: 'right',
          alignItems: 'center'
        }}
      >
        <AdasTypography
          sx={truncationStyles}
          variant='body2'
          color={item.type === 'DISCOUNT' ? theme.typography.error : theme.typography.defaultColor}
        >
          {currencyFormatter(item.total_price)}
        </AdasTypography>
        {isCustomItem && !disabled && (
          <ModeEditOutlineOutlinedIcon
            color='primary'
            onClick={() => onClickEdit({ type, product: item })}
            fontSize='small'
            sx={{ cursor: 'pointer', ml: { xs: '4px', md: '8px' } }}
          />
        )}
      </AdasBox>
    </AdasCard>
  )
}

// Render list of items with header
export const ItemList = ({ itemList, name, isCustomItem = true, disabled, onClickEdit, type, customData }) => {
  const { isMobile } = useDeviceQueries()

  const headerStyle = {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }

  return (
    <AdasBox>
      {itemList?.length > 0 ? (
        <AdasBox>
          <AdasBox
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}
          >
            <AdasBox sx={{ flex: isMobile ? '1 1 25%' : '1 1 50%', minWidth: 0 }}>
              <AdasTypography sx={headerStyle} variant='body2'>
                Name
              </AdasTypography>
            </AdasBox>

            <AdasBox
              sx={{ flex: isMobile ? '1 1 25%' : '1 1 15%', minWidth: 0, display: 'flex', justifyContent: { xs: 'center', md: 'end' } }}
            >
              <AdasTypography sx={headerStyle} variant='body2'>
                Qty
              </AdasTypography>
            </AdasBox>
            <AdasBox sx={{ flex: isMobile ? '1 1 25%' : '1 1 20%', minWidth: 0, display: 'flex', justifyContent: 'end' }}>
              <AdasTypography sx={headerStyle} variant='body2' mr={{ xs: '24px', md: '3rem' }}>
                Price
              </AdasTypography>
            </AdasBox>
          </AdasBox>

          {itemList.map((item, index) => (
            <ItemRow
              key={index}
              item={item}
              isCustomItem={isCustomItem}
              disabled={disabled}
              onClickEdit={onClickEdit}
              isMobile={isMobile}
              type={type}
              customData={customData}
            />
          ))}
        </AdasBox>
      ) : (
        <AdasBox
          sx={{
            height: '106px',
            border: `1px dashed ${theme.palette.divider}`,
            display: 'flex',
            borderRadius: '4px'
          }}
        >
          <AdasTypography sx={{ margin: 'auto' }} variant='body2'>
            NO {name?.toUpperCase()} ADDED
          </AdasTypography>
        </AdasBox>
      )}
    </AdasBox>
  )
}
